import React, { useState, useRef, useEffect } from "react";
import scrollPageToTop from "../../utils/scrollPageToTop";
import usePublishModalTop from "../../utils/usePublishModalTop";



/* Optional per-tab `placeholder`: a component rendered inside the tab button after the
   label — a count, a bell, anything small. It renders inside whatever provider wraps the
   tabs, so it can read context (e.g. useSuper()). Tabs without one are unchanged. */
const InnerTabsm = ({ tabs }) => {
  const safeTabs = tabs.slice(0, 3); // enforce max 3
  const [activeId, setActiveId] = useState(safeTabs[0]?.id);
  const pillRef = useRef(null);
  const btnRefs = useRef({});
  const contentRef = useRef(null);
  const areaRef = useRef(null);

  const active = safeTabs.find((t) => t.id === activeId) || safeTabs[0];
  const ActiveComponent = active?.component || null;

  /* Reset active tab when prop changes*/
  useEffect(() => {
    if (!safeTabs.find((t) => t.id === activeId)) {
      setActiveId(safeTabs[0]?.id);
    }
  }, [tabs, activeId]);

  /* pill */
  useEffect(() => {
    const btn = btnRefs.current[activeId];
    const pill = pillRef.current;
    if (btn && pill) {
      const parent = btn.parentElement.getBoundingClientRect();
      const rect = btn.getBoundingClientRect();
      pill.style.width = `${rect.width}px`;
      pill.style.transform = `translateX(${rect.left - parent.left}px)`;
    }
  }, [activeId]);

  /* New inner tab (or first mount): content panel and page back to the top.
     On mobile the panel scrolls inside its own div (wrapper is 100vh), and that div persists
     across tab changes; TabBar sits above the 100vh wrapper so the window must reset too. */
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    scrollPageToTop(contentRef.current);
  }, [activeId]);

  /* Modals sit UNDER the info bar: publish where the content panel starts (utils/usePublishModalTop). */
  usePublishModalTop(areaRef, [activeId]);

  const handleTabChange = (id) => {
    if (id === activeId) return;
    setActiveId(id);
  };

  /* styles */
  const styles = {
    wrapper: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      padding: "0 16px",
      maxWidth: 500,
      margin: "0 auto",
      width: "100%",
      boxSizing: "border-box",   // height comes from .fr-tab-content (flex column) — no more 100vh + TabBar overflow
    },
    tabBar: {
      position: "relative",
      display: "flex",
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 14,
      padding: 4,
      marginBottom: 12,
      gap: 4,
      flexShrink: 0,
    },
    pill: {
      position: "absolute",
      top: 4,
      left: 0,
      height: "calc(100% - 8px)",
      borderRadius: 11,
      background:
        "linear-gradient(135deg, rgba(34,211,238,0.18) 0%, rgba(59,130,246,0.18) 100%)",
      border: "1px solid rgba(34,211,238,0.3)",
      transition:
        "transform 0.35s cubic-bezier(.4,0,.2,1), width 0.35s cubic-bezier(.4,0,.2,1)",
      pointerEvents: "none",
      zIndex: 0,
    },
    tabBtn: (isActive) => ({
      position: "relative",
      zIndex: 1,
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "10px 6px",
      background: "none",
      border: "none",
      borderRadius: 11,
      cursor: "pointer",
      fontSize: "0.82rem",
      fontWeight: isActive ? 600 : 500,
      color: isActive ? "#22d3ee" : "rgba(255,255,255,0.45)",
      transition: "color 0.25s ease",
      WebkitTapHighlightColor: "transparent",
      whiteSpace: "nowrap",
    }),
    ph: { display: "inline-flex", alignItems: "center" },   // wraps a tab's placeholder
    headerBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 16px",
      marginBottom: 8,
      background:
        "linear-gradient(135deg, rgba(34,211,238,0.08) 0%, rgba(59,130,246,0.08) 100%)",
      border: "1px solid rgba(34,211,238,0.2)",
      borderRadius: 12,
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: "0.95rem",
      fontWeight: 600,
      color: "#e2e8f0",
      margin: 0,
    },
    contentArea: {
      position: "relative",
      flex: 1,
      minHeight: 0,
      borderRadius: 16,
      overflow: "hidden",
      border: "1px solid rgba(34,211,238,0.15)",
      background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    },
    contentInner: {
      width: "100%",
      height: "100%",
      overflowY: "auto",
    },
  };

  return (
    <div style={styles.wrapper}>
      {/* Tab bar */}
      <div style={styles.tabBar}>
        <div ref={pillRef} style={styles.pill} />
        {safeTabs.map((tab) => (
          <button
            key={tab.id}
            ref={(el) => (btnRefs.current[tab.id] = el)}
            style={styles.tabBtn(tab.id === activeId)}
            onClick={() => handleTabChange(tab.id)}
            aria-selected={tab.id === activeId}
            role="tab"
          >
            {tab.icon && (
              <span style={{ display: "flex", alignItems: "center" }}>
                {tab.icon}
              </span>
            )}
            {tab.label}
            {tab.placeholder && (
              <span data-ph="" style={styles.ph}><tab.placeholder /></span>
            )}
          </button>
        ))}
      </div>

      {/* Header bar */}
      <div style={styles.headerBar}>
        <p style={styles.headerTitle}>
          {active.icon && (
            <span
              style={{
                marginRight: 6,
                verticalAlign: "middle",
                display: "inline-flex",
              }}
            >
              {active.icon}
            </span>
          )}
          {active.label}
        </p>
      </div>

      {/* Content area */}
      <div ref={areaRef} style={styles.contentArea}>
        <div ref={contentRef} style={styles.contentInner}>
          {ActiveComponent && <ActiveComponent />}
        </div>
      </div>
    </div>
  );
};

export default InnerTabsm;